import React, { Suspense, lazy, useEffect, useState } from 'react';
import { AppBar, Toolbar, Container, IconButton, Box, Slide, useMediaQuery } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { AccountCircleOutlined, Menu as MenuIcon, Search as SearchIcon } from '@mui/icons-material';
import NavbarLinks from './NavbarLinks';
import Logo from './Logo';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import ContactInfo from './ContactInfo';
import NavBarMobile from './NavBarMobile';
import RequestButton from '../RequestButton/RequestButton';
import { useNavigate } from 'react-router-dom';
import CartIconButton from '../Cart/CartIconButton';
import MobileTopBar from './MobileTopBar';

const SearchBar = lazy(() => import('./SearchBar'));

const Navbar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  const [isSticky, setIsSticky] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showNavbar] = useState(true);
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(null);
  const [shouldRenderSearchBar, setShouldRenderSearchBar] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  // Dynamic text color based on theme mode
  const textColor = theme.palette.mode === 'dark' ? 'white' : theme.palette.text.primary;

  const handleSearchEnter = (event: React.MouseEvent<HTMLElement>) => {
    setShouldRenderSearchBar(true);
    setSearchAnchorEl(event.currentTarget);
  };

  const handleSearchLeave = (event?: React.MouseEvent<HTMLElement>) => {
    const relatedTarget = event?.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !relatedTarget.closest('.MuiPopper-root')) {
      setSearchAnchorEl(null);
    }
  };

  return (
    <>
      {isMobile ? (
        <MobileTopBar
          isSticky={isSticky}
          onOpenMenu={() => toggleDrawer(true)}
          onSearch={() => navigate('/search')}
        />
      ) : (
        <Slide in={showNavbar} direction="down" timeout={{ enter: 250 }}>
        <AppBar
          position="sticky"
          sx={{
            top: 0,
            transition: 'box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out, transform 0.2s ease-in-out',
            boxShadow: isSticky ? 6 : 0,
            transform: isSticky ? 'translateY(0)' : 'translateY(0)',
            backgroundColor: alpha(theme.palette.background.paper, 0),
            color: textColor,
            margin: 0,
            padding: 0,
            width: '100%',
            display: 'flex',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backdropFilter: isSticky ? 'blur(10px)' : 'blur(5px)',
              backgroundColor: alpha(theme.palette.background.paper, 0.6),
              transition: 'backdrop-filter 0.2s ease-in-out, background-color 0.2s ease-in-out',
              zIndex: -1,
            },
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              margin: 0,
              width: '100%',
              p: 0,
            }}
          >
            <Toolbar
              sx={{
                justifyContent: { xs: 'space-between', lg: 'flex-start' },
                alignItems: 'center',
                display: 'flex',
                gap: { xs: '8px', lg: '12px' },
                position: 'relative',
                width: { xs: '100%', lg: '98vw' },
                minHeight: '80px',
                flexWrap: 'nowrap',
              }}
            >
              <Box
                sx={{
                  display: { xs: 'flex', lg: 'none' },
                  alignItems: 'center',
                  width: '48px',
                }}
              >
                <IconButton edge="end" color="inherit" aria-label="menu" onClick={() => toggleDrawer(true)} sx={{ flexShrink: 0 }}>
                  <MenuIcon />
                </IconButton>
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  width: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Logo isSticky={isSticky} textColor={textColor} />
                <ThemeToggle />
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  flexShrink: 1,
                  minWidth: 0,
                  display: { xs: 'none', lg: 'block' },
                }}
              >
                <NavbarLinks isSticky={isSticky} />
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  width: 'auto',
                  display: { xs: 'none', lg: 'block' },
                }}
              >
                <ContactInfo isSticky={isSticky} textColor={textColor} />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexShrink: 0,
                  width: 'auto',
                }}
              >
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                  <RequestButton
                    variant="text"
                    filled={false}
                    label="Оставить заявку"
                    dark={true}
                  />
                </Box>

                <Box onMouseEnter={handleSearchEnter} onMouseLeave={handleSearchLeave} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton edge="end" color="inherit" aria-label="search" size="large" onClick={() => navigate('/search')}>
                    <SearchIcon />
                  </IconButton>
                </Box>

                <IconButton edge="end" color="inherit" aria-label="Личный кабинет" size="large" onClick={() => navigate('/account')}>
                  <AccountCircleOutlined />
                </IconButton>

                <CartIconButton color="inherit" />
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
        </Slide>
      )}

      <NavBarMobile drawerOpen={drawerOpen} toggleDrawer={toggleDrawer} />

      {!isMobile && shouldRenderSearchBar && (
        <Suspense fallback={null}>
          <SearchBar anchorEl={searchAnchorEl} onClose={handleSearchLeave} isOpen={Boolean(searchAnchorEl)} />
        </Suspense>
      )}
    </>
  );
};

export default Navbar;
